/**
 * TanStack Query hook for GET /api/v1/admin/logs (Phase 12.3).
 *
 * The AdminLogsPage drives auto-refresh via the ``refetchInterval`` knob
 * on this hook. The query key includes the filter snapshot so a filter
 * change reissues the request immediately rather than waiting for the
 * next interval tick.
 *
 * Pagination is id-based: pass ``before_id`` to fetch older rows. With
 * ``before_id`` left empty the endpoint always returns the newest rows
 * first, which is exactly what the live tail needs.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type { SystemLogQuery, SystemLogResponse } from "@/types/api";

export const ADMIN_LOGS_QUERY_KEY = ["admin", "logs"] as const;

export interface UseAdminLogsArgs {
  query: SystemLogQuery;
  /**
   * Milliseconds between auto-refresh calls. Pass ``false`` to disable
   * auto-refresh (toggle-off). Defaults to 5_000 ms per SPEC §9.
   */
  refetchInterval?: number | false;
  /** Set to false to keep the query mounted but not fetch. */
  enabled?: boolean;
}

export function useAdminLogs(
  args: UseAdminLogsArgs,
): UseQueryResult<SystemLogResponse[]> {
  const { query, refetchInterval = 5_000, enabled = true } = args;
  return useQuery({
    queryKey: [...ADMIN_LOGS_QUERY_KEY, query],
    queryFn: async () => {
      const response = await api.get<SystemLogResponse[]>("/admin/logs", {
        params: stripEmpty(query),
      });
      return response.data;
    },
    refetchInterval,
    enabled,
  });
}

/** Strip undefined / empty-string params so axios omits them from the URL. */
function stripEmpty(q: SystemLogQuery): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    out[k] = v;
  }
  return out;
}
